import { useEffect, useRef, useState } from "react";
import { connectSocket, releaseSocket } from "../utils/socket";

export default function useGameSocket({
  gameToken,
  gameId,
  onLobbyUpdated,
  onPartnerRequestReceived,
  onPartnerRequestUpdated,
  onPhaseUpdated,
  onTurnUpdated,
  onTurnResultReady,
  onRecoveryUpdated,
  onLeaderboardUpdated,
}) {
  const [connectionStatus, setConnectionStatus] = useState("idle");
  const handlersRef = useRef({
    onLobbyUpdated,
    onPartnerRequestReceived,
    onPartnerRequestUpdated,
    onPhaseUpdated,
    onTurnUpdated,
    onTurnResultReady,
    onRecoveryUpdated,
    onLeaderboardUpdated,
  });
  const lastRefetchAtRef = useRef(0);

  useEffect(() => {
    handlersRef.current = {
      onLobbyUpdated,
      onPartnerRequestReceived,
      onPartnerRequestUpdated,
      onPhaseUpdated,
      onTurnUpdated,
      onTurnResultReady,
      onRecoveryUpdated,
      onLeaderboardUpdated,
    };
  }, [
    onLobbyUpdated,
    onPartnerRequestReceived,
    onPartnerRequestUpdated,
    onPhaseUpdated,
    onTurnUpdated,
    onTurnResultReady,
    onRecoveryUpdated,
    onLeaderboardUpdated,
  ]);

  useEffect(() => {
    if (!gameToken || !gameId) {
      setConnectionStatus("idle");
      return undefined;
    }

    const socket = connectSocket({ gameToken });

    const handleConnect = () => {
      setConnectionStatus("connected");
      socket.emit("game:lobby:join", { gameId });
      socket.emit("game:partner:inbox:subscribe", { gameId });
      socket.emit("game:turn:subscribe", { gameId });
      socket.emit("game:recovery:subscribe", { gameId });
      socket.emit("game:leaderboard:subscribe", { gameId });
    };

    const handleReconnectAttempt = () => {
      setConnectionStatus("reconnecting");
    };

    const emitSafe = (fn, payload) => {
      const now = Date.now();
      if (now - lastRefetchAtRef.current < 250) return;
      lastRefetchAtRef.current = now;
      fn?.(payload);
    };

    if (socket.connected) {
      handleConnect();
    } else {
      setConnectionStatus("reconnecting");
    }

    socket.io.on("reconnect_attempt", handleReconnectAttempt);
    socket.on("connect", handleConnect);
    socket.on("disconnect", () => setConnectionStatus("disconnected"));
    socket.on("connect_error", () => setConnectionStatus("error"));
    socket.on("game:lobby:updated", (payload) =>
      emitSafe(handlersRef.current.onLobbyUpdated, payload),
    );
    socket.on("game:partner:request:received", (payload) =>
      emitSafe(handlersRef.current.onPartnerRequestReceived, payload),
    );
    socket.on("game:partner:request:updated", (payload) =>
      emitSafe(handlersRef.current.onPartnerRequestUpdated, payload),
    );
    socket.on("game:phase:updated", (payload) =>
      emitSafe(handlersRef.current.onPhaseUpdated, payload),
    );
    socket.on("game:turn:updated", (payload) =>
      emitSafe(handlersRef.current.onTurnUpdated, payload),
    );
    socket.on("game:turn:result-ready", (payload) =>
      emitSafe(handlersRef.current.onTurnResultReady, payload),
    );
    socket.on("game:recovery:updated", (payload) =>
      emitSafe(handlersRef.current.onRecoveryUpdated, payload),
    );
    socket.on("game:leaderboard:updated", (payload) =>
      emitSafe(handlersRef.current.onLeaderboardUpdated, payload),
    );

    return () => {
      socket.emit("game:lobby:leave", { gameId });
      socket.removeAllListeners();
      socket.io.off("reconnect_attempt", handleReconnectAttempt);
      releaseSocket();
    };
  }, [gameId, gameToken]);

  return { connectionStatus };
}
